import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertTriangle, FileDown, Loader2, Phone, Mail, DollarSign, UserX } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const DelinquencyReport = () => {
  const [report, setReport] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/delinquency`, { withCredentials: true });
      setReport(data);
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/delinquency/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inadimplencia_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF de inadimplência gerado!');
    } catch (error) { toast.error('Erro ao gerar PDF'); }
    finally { setPdfLoading(false); }
  };

  if (!report) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0F1B3D]" /></div>;
  }

  const { summary, patients } = report;
  const delinquents = patients.filter(p => p.is_delinquent);
  const clean = patients.filter(p => !p.is_delinquent);

  return (
    <div className="space-y-6" data-testid="delinquency-report">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Inadimplência
          </h2>
          <p className="text-[#5F6B85]">Relatório de pagamentos pendentes por paciente</p>
        </div>
        <Button variant="outline" className="border-[#E5DCC8]" onClick={handleDownloadPdf} disabled={pdfLoading} data-testid="download-delinquency-pdf">
          {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
          {pdfLoading ? 'Gerando...' : 'Exportar PDF'}
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-[#E5DCC8]" data-testid="delinquent-count">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#A8174E]/10 rounded-lg"><UserX className="h-5 w-5 text-[#A8174E]" /></div>
              <div>
                <p className="text-xs text-[#7A85A0]">Inadimplentes</p>
                <p className="text-2xl font-bold text-[#A8174E]">{summary.delinquent_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="total-debt">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#A8174E]/10 rounded-lg"><DollarSign className="h-5 w-5 text-[#A8174E]" /></div>
              <div>
                <p className="text-xs text-[#7A85A0]">Dívida Total</p>
                <p className="text-2xl font-bold text-[#A8174E]">R$ {summary.total_debt.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="total-pending-summary">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#E9C46A]/10 rounded-lg"><DollarSign className="h-5 w-5 text-[#E9C46A]" /></div>
              <div>
                <p className="text-xs text-[#7A85A0]">Pendente</p>
                <p className="text-2xl font-bold text-[#E9C46A]">R$ {summary.total_pending.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5DCC8]" data-testid="total-overdue-summary">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#A8174E]/10 rounded-lg"><AlertTriangle className="h-5 w-5 text-[#A8174E]" /></div>
              <div>
                <p className="text-xs text-[#7A85A0]">Atrasado</p>
                <p className="text-2xl font-bold text-[#A8174E]">R$ {summary.total_overdue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delinquent Patients */}
      {delinquents.length > 0 && (
        <div>
          <h3 className="text-xl font-medium text-[#A8174E] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pacientes com Pendências ({delinquents.length})
          </h3>
          <div className="space-y-3">
            {delinquents.map((p) => (
              <Card key={p.patient_id} className="border-[#A8174E]/30" data-testid={`delinquent-${p.patient_id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-[#0F1B3D]">{p.name}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.status === 'active' ? 'bg-[#0F1B3D]/10 text-[#0F1B3D]' : 'bg-[#7A85A0]/10 text-[#7A85A0]'}`}>
                          {p.status === 'active' ? 'Ativo' : p.status === 'inactive' ? 'Inativo' : p.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-[#5F6B85]">
                        {p.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}</span>}
                        {p.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{p.email}</span>}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xs text-[#7A85A0]">Faturado</p>
                        <p className="font-semibold text-[#0F1B3D]">R$ {p.total_billed.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#7A85A0]">Pago</p>
                        <p className="font-semibold text-[#0F1B3D]">R$ {p.total_paid.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#7A85A0]">Dívida</p>
                        <p className="font-bold text-[#A8174E] text-lg">R$ {p.total_debt.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {delinquents.length === 0 && (
        <Card className="border-[#0F1B3D]">
          <CardContent className="p-8 text-center">
            <DollarSign className="h-12 w-12 text-[#0F1B3D] mx-auto mb-3" />
            <p className="text-[#0F1B3D] font-semibold">Nenhum paciente inadimplente!</p>
            <p className="text-sm text-[#7A85A0] mt-1">Todos os pagamentos estão em dia</p>
          </CardContent>
        </Card>
      )}

      {/* Clean patients */}
      {clean.length > 0 && (
        <div>
          <h3 className="text-xl font-medium text-[#0F1B3D] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pacientes em Dia ({clean.length})
          </h3>
          <div className="space-y-2">
            {clean.map((p) => (
              <Card key={p.patient_id} className="border-[#E5DCC8]" data-testid={`clean-${p.patient_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#0F1B3D]">{p.name}</span>
                    <span className="text-sm text-[#0F1B3D]">R$ {p.total_paid.toFixed(2)} pago</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
